import { ProductLine, ProcessStep, QuoteRequest, MaterialLayout } from "../types";
import { computeAssemblyPrice } from "./computeAssemblyPrice";
import { computeDiscount } from "./computeDiscount";
import { ComputationOptions } from "./computeMaterialPrice";
import { OptimizedMachineTimes } from "./computeOptimizedMachineTimes";
import { OptimizedMaterialCosts } from "./computeOptimizedMaterialCosts";
import { computeProcessesPrice } from "./computeProcessesPrice";
import { computeProductsPrice } from "./computeProductsPrice";
import { QuoteRequestLineForComputing } from "./computeQuoteRequestLinePrice";
import { computeSalesAdministrationPrice } from "./computeSalesAdministrationPrice";
import { sortQuoteRequestLines } from "./sortQuoteRequestLines";

export type ComputeQuoteRequestPriceParams = {
  quoteRequest: QuoteRequest & {
    quoteRequestLines: QuoteRequestLineForComputing[];
    productLines: ProductLine[];
    processSteps: (ProcessStep & {
      materialLayout: MaterialLayout | null;
      productLines: ProductLine[];
    })[];
  };
  isQuotePrice?: boolean;
  optimizedMachineTimes: OptimizedMachineTimes;
  optimizedMaterialCosts: OptimizedMaterialCosts;
};

export const computeQuoteRequestPrice = (
  {
    quoteRequest,
    isQuotePrice,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  }: ComputeQuoteRequestPriceParams,
  options: ComputationOptions,
): {
  totalBeforeSalesMarginAndDiscount: {
    price: number;
    cost: number;
    margin: number;
  };
  totalLinesBeforeDiscount: {
    price: number;
    cost: number;
    margin: number;
  };
  totalBeforeDiscount: {
    price: number;
    cost: number;
    margin: number;
  };
  seriesPrice: number;
  salesAdministrationPrice: number;
  salesAdministrationCost: number;
  salesAdministrationMargin: number;
  totalPriceBeforeDiscount: number;
  discount: number;
  totalPrice: number;
  totalSalesMargin: number;
} => {
  let quoteRequestLinesPrice = 0;
  let quoteRequestLinesCost = 0;
  let quoteRequestLinesMargin = 0;
  let totalSalesMargin = 0;

  const { batches } = sortQuoteRequestLines(quoteRequest.quoteRequestLines);

  batches.forEach((assemblies) => {
    const assembly = assemblies[0];

    if (!assembly) {
      return;
    }

    const {
      assembly: { seriesPrice, seriesCost, seriesMargin, seriesPriceWithSalesMargin },
    } = computeAssemblyPrice(
      {
        children: assembly.children,
        quoteRequestLine: assembly.quoteRequestLine,
        isQuotePrice,
        salesMarginRatio: quoteRequest.salesMarginRatio || 0,
        optimizedMachineTimes,
        optimizedMaterialCosts,
      },
      options,
    );

    quoteRequestLinesPrice += seriesPrice;
    quoteRequestLinesCost += seriesCost;
    quoteRequestLinesMargin += seriesMargin;
    totalSalesMargin += seriesPriceWithSalesMargin - seriesPrice;
  });

  const { salesAdministrationCost, salesAdministrationMargin, salesAdministrationPrice } =
    computeSalesAdministrationPrice({
      salesAdministrationTime: quoteRequest.salesAdministrationTime,
      salesAdministrationHourlyRate: quoteRequest.salesAdministrationHourlyRate,
      salesAdministrationMarginRatio: quoteRequest.salesAdministrationMarginRatio,
    });

  const { productsPrice, productsMargin, productsCost } = computeProductsPrice({
    productLines: quoteRequest.productLines,
    productsMarginRatio: 0, // TODO: This is a bug but for now it has no impact. We should add a column productsMarginRatio in quote request
    quantityInAssembly: null,
  });

  const {
    partProcessesPrice: quoteRequestProcessesPrice,
    partProcessesCost: quoteRequestProcessesCost,
    partProcessesMarginPrice: quoteRequestProcessesMarginPrice,
  } = computeProcessesPrice({
    quantity: 1,
    reworkRatio: null,
    quantityInAssembly: null,
    addedValueMarginRatio: null,
    productsMarginRatio: null,
    processSteps: quoteRequest.processSteps,
    optimizedMachineTimes,
  });

  const quoteRequestPrice = quoteRequestProcessesPrice + quoteRequestLinesPrice;
  const quoteRequestCost = quoteRequestProcessesCost + quoteRequestLinesCost;
  const quoteRequestMargin = quoteRequestProcessesMarginPrice + quoteRequestLinesMargin;

  const certificateCost = quoteRequest.certificateCost ?? 0;
  const seriesPrice = quoteRequestPrice + totalSalesMargin;

  const totalCostBeforeSalesMarginAndDiscount =
    quoteRequestCost + productsCost + salesAdministrationCost + certificateCost;
  const totalMarginBeforeSalesMarginAndDiscount =
    quoteRequestMargin + productsMargin + salesAdministrationMargin;
  const totalPriceBeforeSalesMarginAndDiscount =
    quoteRequestPrice + productsPrice + salesAdministrationPrice + certificateCost;

  const totalCostBeforeDiscount = totalCostBeforeSalesMarginAndDiscount + totalSalesMargin;
  const totalPriceBeforeDiscount = totalPriceBeforeSalesMarginAndDiscount + totalSalesMargin;

  const { discount, totalPriceAfterDiscount } = computeDiscount({
    discountRatio: quoteRequest.discountRatio,
    totalPriceBeforeDiscount,
  });

  return {
    totalBeforeSalesMarginAndDiscount: {
      price: totalPriceBeforeSalesMarginAndDiscount,
      cost: totalCostBeforeSalesMarginAndDiscount,
      margin: totalMarginBeforeSalesMarginAndDiscount,
    },
    totalLinesBeforeDiscount: {
      price: quoteRequestLinesPrice + totalSalesMargin,
      cost: quoteRequestLinesCost + totalSalesMargin,
      margin: quoteRequestLinesMargin,
    },
    totalBeforeDiscount: {
      price: totalPriceBeforeDiscount,
      cost: totalCostBeforeDiscount,
      margin: totalPriceBeforeDiscount - totalCostBeforeDiscount,
    },
    seriesPrice,
    salesAdministrationPrice,
    salesAdministrationCost,
    salesAdministrationMargin,
    totalPriceBeforeDiscount,
    discount,
    totalPrice: totalPriceAfterDiscount,
    totalSalesMargin,
  };
};
